import { safeRedirectPath } from '#shared/utils/safe-redirect'

// Create a global auth instance reference
let authInstance: ReturnType<typeof createAuthInstance> | null = null

function createAuthInstance() {
  const { loggedIn, user, session, clear, fetch: refreshSession } = useUserSession()

  // Computed properties. The leads-api JWT stays on the server; see useApi
  const userRoles = computed(() => user.value?.roles || [])
  const isAdmin = computed(() => userRoles.value.includes('ROLE_ADMIN'))
  const isValidator = computed(() => userRoles.value.includes('ROLE_ADMIN') || userRoles.value.includes('ROLE_VALIDATOR'))
  
  // Profile loading watcher
  const profile = useProfileStore()
  
  watch([loggedIn, user], async ([newLoggedIn, newUser]) => {
    if (newLoggedIn && newUser?.id) {
      try {
        // Check if profile already loaded for this user
        const currentProfileId = profile.getId || null
        if (currentProfileId !== newUser.id) {
          await profile.loadProfile(newUser.id)
        }
      } catch (error) {
        console.error('Failed to load profile:', error)
      }
    } else if (!newLoggedIn) {
      profile.clearProfile()
    }
  }, { immediate: true })
  
  // Logout function: revokes the leads-api session, then tells other tabs
  const logout = async ({ returnTo }: { returnTo?: string } = {}) => {
    try {
      await $fetch('/api/auth/logout', { method: 'POST' })
    } catch (error) {
      console.error('Logout request failed:', error)
    }
    await clear()
    localStorage.setItem('auth_logout', String(Date.now()))
    const redirect = safeRedirectPath(returnTo)
    await navigateTo(redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login')
  }

  return {
    loggedIn: readonly(loggedIn),
    user: readonly(user),
    session: readonly(session),
    userRoles: readonly(userRoles),
    isAdmin: readonly(isAdmin),
    isValidator: readonly(isValidator),
    clear,
    logout,
    refreshSession
  }
}

export const useAuth = () => {
  if (!authInstance) {
    authInstance = createAuthInstance()
  }
  
  return authInstance
}